import React, { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, UserPlus, CalendarClock, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  Button, StatusBadge, DomainDot, ICON_PX,
  Tooltip, TooltipTrigger, TooltipContent,
} from '../ui';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { usePracticeManagement } from '../../contexts/PracticeManagementContext';
import { MiniCalendar } from '../manager/OperationalFocusTabs';

const TIER_ORDER = { A: 0, B: 1, C: 2, D: 3 };
const TIER_VARIANT = { A: 'critical', B: 'watch', C: 'monitoring', D: 'monitoring' };

function OrphanedTaskModal({ open, onOpenChange }) {
  const { orphanedTasks, staffRoster, reassignOrphanedTask, deferOrphanedTask } = usePracticeManagement();
  const [deferTaskId, setDeferTaskId] = useState(null);
  const [deferDate, setDeferDate] = useState('');
  const [deferReason, setDeferReason] = useState('');

  const sorted = useMemo(() =>
    [...orphanedTasks].sort((a, b) => (TIER_ORDER[a.criticalityTier] || 3) - (TIER_ORDER[b.criticalityTier] || 3)),
    [orphanedTasks]
  );

  useEffect(() => {
    if (open) { setDeferTaskId(null); setDeferDate(''); setDeferReason(''); }
  }, [open]);

  const availableStaff = useMemo(() => (staffRoster || []).filter((s) => s.available), [staffRoster]);

  const handleAssign = (taskId, staff) => {
    reassignOrphanedTask(taskId, { name: staff.name, id: staff.id });
    if (sorted.length <= 1) onOpenChange(false);
  };

  const handleDeferConfirm = (taskId) => {
    if (!deferDate || !deferReason) return;
    deferOrphanedTask(taskId, deferDate, deferReason);
    setDeferTaskId(null);
    setDeferDate('');
    setDeferReason('');
    if (sorted.length <= 1) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={ICON_PX.md} className="text-red-critical" />
            Unassigned Tasks
          </DialogTitle>
          <DialogDescription>
            {sorted.length} task{sorted.length !== 1 ? 's' : ''} need an owner. Assign to a staff member or defer to a later date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 px-6 py-2 max-h-[60vh] overflow-y-auto">
          {sorted.map((task) => {
            const isTierA = task.criticalityTier === 'A';
            const isDeferring = deferTaskId === task.id;

            return (
              <div key={task.id} className="rounded-lg border border-nn-border bg-nn-white overflow-hidden">
                {/* Task row */}
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <DomainDot domain={task.domain} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-body-sm font-medium text-text-dark truncate">{task.taskName}</span>
                      <StatusBadge variant={TIER_VARIANT[task.criticalityTier] || 'monitoring'}>
                        Tier {task.criticalityTier}
                      </StatusBadge>
                    </div>
                    <div className="text-caption text-text-muted mt-0.5">
                      {task.ownerRole?.replace(/_/g, ' ')} · Due {task.dueTime} · {task.domain}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Assign — inline dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline">
                          <UserPlus size={ICON_PX.md} className="mr-1" /> Assign
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[200px] max-h-[240px] overflow-y-auto">
                        <DropdownMenuLabel className="text-caption">Assign to</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {availableStaff.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => handleAssign(task.id, s)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-body-sm hover:bg-nn-bg-subtle rounded-sm transition-colors"
                          >
                            <span className="font-medium text-text-dark">{s.name}</span>
                            <span className="text-text-muted capitalize">{s.role.replace(/_/g, ' ')}</span>
                          </button>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Defer — calendar dropdown */}
                    {isTierA ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button size="sm" variant="outline" disabled>
                              <CalendarClock size={ICON_PX.md} className="mr-1" /> Defer
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" maxWidth={200}>
                          Tier A tasks cannot be deferred — must be assigned same day.
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <div className="relative">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setDeferTaskId(isDeferring ? null : task.id); setDeferDate(''); setDeferReason(''); }}
                          className={isDeferring ? 'ring-1 ring-aqua' : ''}
                        >
                          <CalendarClock size={ICON_PX.md} className="mr-1" /> Defer
                        </Button>
                        {isDeferring && (
                          <>
                            <div className="fixed inset-0 z-[1049]" onClick={() => setDeferTaskId(null)} />
                            <div className="absolute top-full mt-2 right-0 z-[1050] bg-nn-white rounded-lg p-3 shadow-lg border border-nn-border" style={{ minWidth: 280 }}>
                              <MiniCalendar value={deferDate} onChange={setDeferDate} />
                              <div className="mt-2">
                                <label className="block text-caption text-text-muted mb-1">Reason</label>
                                <input
                                  type="text"
                                  value={deferReason}
                                  onChange={(e) => setDeferReason(e.target.value)}
                                  placeholder="Reason for deferral..."
                                  className="w-full rounded-md border border-nn-border bg-nn-white px-2.5 py-1.5 text-body-sm focus:outline-none focus:ring-2 focus:ring-aqua/40"
                                />
                              </div>
                              <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-nn-border">
                                <Button size="sm" variant="outline" onClick={() => setDeferTaskId(null)}>Cancel</Button>
                                <Button size="sm" variant="outline" disabled={!deferDate || !deferReason} onClick={() => handleDeferConfirm(task.id)}>
                                  <Check size={ICON_PX.md} className="mr-1" /> Confirm
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {sorted.length === 0 && (
            <div className="text-body-sm text-text-muted text-center py-6">
              All tasks have been assigned. You can close this dialog.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OrphanedTaskModal;
